import { useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { HBox } from '../../assets/SharedComponents';
import { TextField, InputAdornment, IconButton, ButtonGroup, Button, Box, Typography, Checkbox } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import AutocompleteGooglePlaces from "Components/Common/AutocompleteGooglePlaces";

export const emptySecondParentValues = {
    parent2FirstName: '',
    parent2LastName: '',
    parent2Id: '',
    parent2IdType: '1',
    parent2Phone: '',
    parent2Email: '',
    parent2City: '',
    parent2Street: '',
    parent2StreetNo: '',
    parent2SameAddress: true,
    parent2Password: '',
    parent2ConfirmPassword: ''
};

export const getSecondParentInitialValues = (source = {}) => {
    const values = {};
    Object.keys(emptySecondParentValues).forEach(key => {
        values[key] = source[key] !== undefined && source[key] !== null && source[key] !== '' ? source[key] : emptySecondParentValues[key];
    });
    return values;
};

// Final parent 2 values for submit: cleared when there's no second parent,
// and the address is copied from parent 1 when "same address" is checked.
export const resolveSecondParentValues = (values) => {
    if (!values.thereIsParent2) {
        return { ...values, ...emptySecondParentValues };
    }
    if (values.parent2SameAddress) {
        return { ...values, parent2City: values.city, parent2Street: values.street, parent2StreetNo: values.streetNo };
    }
    return values;
};

const isFullAddress = (city, street, streetNo) => Boolean(city && street && streetNo);

// Same rules as the first parent's fields, enforced only when "thereIsParent2" is checked.
// parent1Keys maps the first parent's field names so parent 2 can't duplicate them.
export const getSecondParentValidation = (t, { parent1Keys = { id: 'id', phone: 'phone' } } = {}) => {
    const whenParent2 = (schema) => yup.string().when('thereIsParent2', {
        is: true,
        then: schema,
        otherwise: yup.string().nullable(),
    });

    return {
        thereIsParent2: yup.boolean(),
        // Parent 2's address must include city, street and house number - either their own,
        // or parent 1's when "same address" is checked (since that's what gets copied).
        parent2SameAddress: yup.boolean()
            .test('parent1-full-address', t('parent1AddressIncomplete'), function (sameAddress) {
                const { thereIsParent2, city, street, streetNo } = this.parent;
                return !thereIsParent2 || !sameAddress || isFullAddress(city, street, streetNo);
            }),
        parent2FirstName: whenParent2(yup
            .string(t("firstName"))
            .required(t("mandatoryField"))
            .min(2, t('insertCorrectName'))
            .matches(/^[a-z\s֐-׾]+$/i, t('insertCorrectName'))),
        parent2LastName: whenParent2(yup
            .string(t("lastName"))
            .required(t("mandatoryField"))
            .min(2, t('insertCorrectLName'))
            .matches(/^[a-z\s֐-׾]+$/i, t('insertCorrectLName'))),
        parent2Id: whenParent2(yup
            .string(t('id'))
            .required(t("mandatoryField"))
            .min(8, t('enterValidValue'))
            .max(30, t('enterValidValue'))
            .matches(/^[0-9]*$/, t('enterValidValue'))
            .test('not-same-as-parent1', t('parent2IdSameAsParent1'), function (value) {
                return value !== this.parent[parent1Keys.id];
            })),
        parent2Phone: whenParent2(yup
            .string(t("phoneNumber"))
            .required(t("mandatoryField"))
            .min(9, t("insertValidPhone"))
            .max(10, t("insertValidPhone"))
            .matches(/^[0-9]*$/, t('insertValidPhone'))
            .test('not-same-as-parent1', t('parent2PhoneSameAsParent1'), function (value) {
                return value !== this.parent[parent1Keys.phone];
            })),
        parent2Email: whenParent2(yup
            .string(t("email"))
            .email(t("insertValidMail"))
            .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, t("insertValidMail"))
            .required(t("mandatoryField"))),
        parent2StreetNo: yup.string().when(['thereIsParent2', 'parent2SameAddress'], {
            is: (thereIsParent2, sameAddress) => thereIsParent2 && !sameAddress,
            then: yup.string()
                .test('full-address', t('fullAddressRequired'), function (value) {
                    return isFullAddress(this.parent.parent2City, this.parent.parent2Street, value);
                })
                .matches(/^\d+(\/\d+)*$/, { message: t("streetNumber"), excludeEmptyString: true }),
            otherwise: yup.string().nullable(),
        }),
        parent2Password: whenParent2(yup
            .string(t("password"))
            .required(t("mandatoryField"))
            .min(8, t("passwordRule"))
            .matches(/^(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{8,}$/, t("passwordRule"))),
        parent2ConfirmPassword: whenParent2(yup
            .string(t("confirmPassword"))
            .oneOf([yup.ref('parent2Password'), null], t("matchPasswords"))),
    };
};

// Submit-time checks matching the first parent's; returns a translation key, or null when valid.
export const getSecondParentSubmitError = (values, isIsraeliId) => {
    if (!values.thereIsParent2) return null;
    if (!isIsraeliId(values.parent2Id) && values.parent2IdType === "1") return "wrongId";
    if (values.parent2Id.toString().length < 9) {
        if (values.parent2IdType === "3") return "wrongJId";
        if (values.parent2IdType === "1") return "wrongId";
    }
    if (values.parent2Email.includes('zofim.org.il')) return "emailDomain";
    return null;
};

export const SecondParentFields = ({ formik, classes, IdTextByType }) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const inputRefId = useRef();
    const inputRefPhone = useRef();

    const addressChanged = (oAddress) => {
        formik.setValues({ ...formik.values, parent2Street: oAddress.StreetName, parent2StreetNo: oAddress.StreetNum, parent2City: oAddress.CityName });
    }

    const passwordAdornment = (show, toggle) => (
        <InputAdornment position="end">
            <IconButton aria-label="toggle password visibility" onClick={toggle} onMouseDown={e => e.preventDefault()}>
                {show ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <>
            <HBox width={'100%'} className={` ${classes.marginBottom1}`} alignItems="center">
                <Checkbox checked={formik.values.thereIsParent2} onChange={formik.handleChange} id="thereIsParent2" name="thereIsParent2"
                    icon={<CheckBoxOutlineBlankOutlinedIcon sx={{ color: '#3a7735' }} />}
                    checkedIcon={<CheckBoxOutlinedIcon sx={{ color: '#3a7735' }} />}
                />
                <Typography>{t("thereIsParent")}</Typography>
            </HBox>
            {formik.values.thereIsParent2 && <>
                <HBox width={'100%'} className={` ${classes.marginBottom1}`}>
                    <TextField
                        label={`${t("firstName")} ${t("secParent")}`}
                        className={`${classes.marginBottom1} ${classes.TextfieldILabelStyle}`}
                        id="parent2FirstName"
                        name="parent2FirstName"
                        style={{ width: "100%" }}
                        value={formik.values.parent2FirstName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.parent2FirstName && Boolean(formik.errors.parent2FirstName)}
                        helperText={formik.touched.parent2FirstName && formik.errors.parent2FirstName}
                        variant="outlined"
                    />
                </HBox>
                <HBox width={'100%'} className={` ${classes.marginBottom1}`}>
                    <TextField
                        label={`${t("lastName")} ${t("secParent")}`}
                        className={`${classes.marginBottom1} ${classes.TextfieldILabelStyle}`}
                        id="parent2LastName"
                        name="parent2LastName"
                        style={{ width: "100%" }}
                        value={formik.values.parent2LastName}
                        onChange={formik.handleChange}
                        // Suggest parent 1's last name; the user can still edit it
                        onFocus={() => {
                            if (!formik.values.parent2LastName && formik.values.lastName) {
                                formik.setFieldValue('parent2LastName', formik.values.lastName);
                            }
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.parent2LastName && Boolean(formik.errors.parent2LastName)}
                        helperText={formik.touched.parent2LastName && formik.errors.parent2LastName}
                        variant="outlined"
                    />
                </HBox>
                <ButtonGroup color="primary" aria-label="outlined primary button group" className={`${classes.IdentificationBtn} ${classes.marginBottom1}`}>
                    <Button className={`${formik.values.parent2IdType === "1" ? classes.selectedBtn : classes.IdnBtn} ${classes.width8}`} onClick={e => formik.setValues({ ...formik.values, parent2IdType: "1" })}>{IdTextByType[1]}</Button>
                    <Button className={`${formik.values.parent2IdType === "2" ? classes.selectedBtn : classes.IdnBtn} ${classes.width6}`} onClick={e => formik.setValues({ ...formik.values, parent2IdType: "2" })}>{IdTextByType[2]}</Button>
                    <Button className={`${formik.values.parent2IdType === "3" ? classes.selectedBtn : classes.IdnBtn} ${classes.width8}`} onClick={e => formik.setValues({ ...formik.values, parent2IdType: "3" })}>{IdTextByType[3]}</Button>
                </ButtonGroup>
                <HBox width={'100%'} className={`${classes.marginBottom1}`}>
                    <TextField
                        label={`${IdTextByType[formik.values.parent2IdType]} ${t("secParent")}`}
                        className={`${classes.marginBottom1} ${classes.TextfieldILabelStyle} ${classes.RemoveTextFieldNumberArrow}`}
                        id="parent2Id"
                        name="parent2Id"
                        style={{ width: "100%" }}
                        value={formik.values.parent2Id}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.parent2Id && Boolean(formik.errors.parent2Id)}
                        helperText={formik.touched.parent2Id && formik.errors.parent2Id}
                        InputProps={{
                            inputRef: inputRefId,
                            inputProps: {
                                inputMode: 'tel',
                                pattern: "[0-9]*"
                            }
                        }}
                        onWheel={(e) => {
                            if (inputRefId && inputRefId.current && inputRefId.current.blur) {
                                inputRefId.current.blur();
                            }
                        }}
                        variant="outlined"
                    />
                </HBox>
                <HBox width={'100%'} className={` ${classes.marginBottom1}`}>
                    <TextField
                        style={{ width: "100%" }}
                        className={`${classes.marginBottom1} ${classes.TextfieldILabelStyle} ${classes.RemoveTextFieldNumberArrow}`}
                        label={`${t("myPhone")} ${t("secParent")}`}
                        InputProps={{
                            inputRef: inputRefPhone,
                            inputProps: {
                                inputMode: 'tel',
                                pattern: "[0-9]*"
                            }
                        }}
                        onWheel={(e) => {
                            if (inputRefPhone && inputRefPhone.current && inputRefPhone.current.blur) {
                                inputRefPhone.current.blur();
                            }
                        }}
                        type="number"
                        id="parent2Phone"
                        name="parent2Phone"
                        value={formik.values.parent2Phone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.parent2Phone && Boolean(formik.errors.parent2Phone)}
                        helperText={formik.touched.parent2Phone && formik.errors.parent2Phone}
                        variant="outlined"
                    />
                </HBox>
                <HBox width={'100%'} className={` ${classes.marginBottom1}`}>
                    <TextField
                        className={`${classes.marginBottom1} ${classes.TextfieldILabelStyle}`}
                        label={`${t("emaillong")} ${t("secParent")}`}
                        style={{ width: "100%" }}
                        id="parent2Email"
                        name="parent2Email"
                        value={formik.values.parent2Email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.parent2Email && Boolean(formik.errors.parent2Email)}
                        helperText={formik.touched.parent2Email && formik.errors.parent2Email}
                        variant="outlined"
                        InputProps={{
                            inputProps: {
                                inputMode: 'url'
                            }
                        }}
                    />
                </HBox>
                <HBox width={'100%'} className={` ${classes.marginBottom1}`} alignItems="center">
                    <Checkbox checked={formik.values.parent2SameAddress} onChange={formik.handleChange} id="parent2SameAddress" name="parent2SameAddress"
                        icon={<CheckBoxOutlineBlankOutlinedIcon sx={{ color: '#3a7735' }} />}
                        checkedIcon={<CheckBoxOutlinedIcon sx={{ color: '#3a7735' }} />}
                    />
                    <Box>
                        <Typography>{t("sameAddressAsParent1")}</Typography>
                        {formik.values.parent2SameAddress && formik.values.city &&
                            <Typography sx={{ fontSize: "0.875rem", color: "#a2a2a2" }}>
                                {`${formik.values.street} ${formik.values.streetNo}, ${formik.values.city}`}
                            </Typography>}
                        {formik.submitCount > 0 && formik.errors.parent2SameAddress &&
                            <Typography sx={{ fontSize: "0.75rem", color: "#d32f2f" }}>{formik.errors.parent2SameAddress}</Typography>}
                    </Box>
                </HBox>
                {!formik.values.parent2SameAddress && <HBox width={'100%'} className={` ${classes.marginBottom1}`}>
                    <AutocompleteGooglePlaces
                        id="parent2Address"
                        name="parent2Address"
                        onPlaceSelected={(placeDetails) => addressChanged(placeDetails.addressDetails)}
                        className={`${classes.marginBottom1} ${classes.TextfieldILabelStyle}`}
                        noOptionsText={t("addressNoOption")}
                        onBlur={formik.handleBlur}
                        inputLabel={`${t('cityStreetAndHouseNum')} ${t("secParent")}`}
                        inputPlaceholder={t("addressSearchPlaceholder")}
                        inputError={(formik.touched.parent2Address || formik.touched.parent2StreetNo) && Boolean(formik.errors.parent2StreetNo)}
                        inputHelperText={(formik.touched.parent2Address || formik.touched.parent2StreetNo) && formik.errors.parent2StreetNo}
                    />
                </HBox>}
                <HBox width={'100%'} className={` ${classes.marginBottom1}`}>
                    <TextField
                        className={`${classes.marginBottom1} ${classes.TextfieldILabelStyle}`}
                        label={`${t('choosePassword')} ${t("secParent")}`}
                        type={showPassword ? "text" : "password"}
                        style={{ width: "100%" }}
                        id="parent2Password"
                        name="parent2Password"
                        placeholder={t("passwordRule")}
                        value={formik.values.parent2Password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.parent2Password && Boolean(formik.errors.parent2Password)}
                        helperText={formik.touched.parent2Password && formik.errors.parent2Password}
                        variant="outlined"
                        InputProps={{ endAdornment: passwordAdornment(showPassword, () => setShowPassword(!showPassword)) }}
                    />
                </HBox>
                <HBox width={'100%'} className={` ${classes.marginBottom1}`}>
                    <TextField
                        placeholder={t("matchPasswords")}
                        className={`${classes.marginBottom1} ${classes.TextfieldILabelStyle}`}
                        label={`${t('choosePassword1')} ${t("secParent")}`}
                        type={showConfirmPassword ? "text" : "password"}
                        style={{ width: "100%" }}
                        id="parent2ConfirmPassword"
                        name="parent2ConfirmPassword"
                        value={formik.values.parent2ConfirmPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.parent2ConfirmPassword && Boolean(formik.errors.parent2ConfirmPassword)}
                        helperText={formik.touched.parent2ConfirmPassword && formik.errors.parent2ConfirmPassword}
                        variant="outlined"
                        InputProps={{ endAdornment: passwordAdornment(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword)) }}
                    />
                </HBox>
            </>}
        </>
    );
}
